import { useState, useEffect, useMemo } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getTodayKST } from "@/shared/lib/date";
import { PageContainer } from "@/components/shared/page-container";
import { Header } from "@/components/shared/header";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCompetitionGamesForList,
  useCompetitionGroups,
  useCreateCompetitionGame,
  useDeleteCompetitionGame,
} from "@/lib/api/competition/hooks";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import type { CompetitionGame, GameStatus } from "@/lib/api/competition/types";
import { GameStatusBadge } from "@/entities/competition";
import { CreateGameDialog } from "@/widgets/competition";

function CompetitionPage() {
  const navigate = useNavigate();

  // 현재 사용자의 officeId
  const [officeId, setOfficeId] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("growing-teacher-user");
    if (stored) {
      try {
        const user = JSON.parse(stored);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOfficeId(user.office?._id || user.office);
      } catch {
        // ignore
      }
    }
  }, []);

  // 게임 목록 (ready/in_progress + 최근 7일 done)
  const { data: games, isLoading: isLoadingGames } = useCompetitionGamesForList();

  // 그룹 목록
  const { data: groups, isLoading: isLoadingGroups } = useCompetitionGroups(
    officeId ?? undefined,
  );

  useLoadingIndicator(isLoadingGames || isLoadingGroups);

  // 게임 생성 다이얼로그
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [loadDate, setLoadDate] = useState<string>("");

  const createGame = useCreateCompetitionGame();
  const deleteGame = useDeleteCompetitionGame();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  // 선택된 그룹에 오늘 날짜 게임이 있는지
  const hasTodayGame = useMemo(() => {
    if (!selectedGroupId || !games) return false;
    const today = getTodayKST();
    return games.some(
      (g) =>
        g.group._id === selectedGroupId &&
        new Date(g.date).toISOString().split("T")[0] === today,
    );
  }, [selectedGroupId, games]);

  // 진행 중인 게임 (ready, in_progress)
  const activeGames = games?.filter(
    (g) => g.status === "ready" || g.status === "in_progress",
  );

  // 완료된 게임 (done)
  const completedGames = games?.filter((g) => g.status === "done");

  const handleCreateGame = async () => {
    if (!selectedGroupId) {
      toast.error("그룹을 선택해주세요.");
      return;
    }

    try {
      const result = await createGame.mutateAsync({
        groupId: selectedGroupId,
        promotionCount: 0,
        leagues: [{ name: "A", playerCount: 4 }],
        previousGameDate: loadDate || undefined,
      });
      toast.success("게임이 생성되었습니다.");
      setIsCreateDialogOpen(false);
      setSelectedGroupId("");
      setLoadDate("");
      navigate({ to: `/competition/${result.data._id}` });
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      toast.error(
        axiosError.response?.data?.message || "게임 생성에 실패했습니다.",
      );
    }
  };

  const handleGameClick = (game: CompetitionGame) => {
    navigate({ to: `/competition/${game._id}` });
  };

  const handleDeleteGame = (game: CompetitionGame) => {
    const gameDate = new Date(game.date).toLocaleDateString("ko-KR");
    setAlertDialogState({
      title: "게임 삭제",
      description: `"${game.group.name}" (${gameDate}) 게임을 삭제하시겠습니까? 이 게임의 점수 기록도 함께 삭제되며 되돌릴 수 없습니다.`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteGame.mutateAsync(game._id);
          toast.success("게임이 삭제되었습니다.");
        } catch (error) {
          const axiosError = error as {
            response?: { data?: { message?: string } };
          };
          toast.error(
            axiosError.response?.data?.message || "삭제에 실패했습니다.",
          );
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  return (
    <PageContainer>
      <Header
        title="Competition"
        description="1:1 단어 대결 게임"
        right={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/competition/groups" })}
            >
              그룹 관리
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />새 게임
            </Button>
          </div>
        }
      />

      {/* 진행 중인 게임 */}
      {!activeGames?.length ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-muted-foreground mb-4">
            진행 중인 게임이 없습니다.
          </p>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />새 게임 만들기
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>지점</TableHead>
              <TableHead>반</TableHead>
              <TableHead></TableHead>
              <TableHead className="text-right">생성 일자</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeGames.map((game, index) => (
              <TableRow
                key={game._id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleGameClick(game)}
              >
                <TableCell className="h-13 min-w-21 p-2 text-muted-foreground">
                  {index + 1}
                </TableCell>
                <TableCell className="h-13 min-w-21 p-2">
                  {game.office.name}
                </TableCell>
                <TableCell className="h-13 min-w-21 p-2">
                  {game.group.name}
                </TableCell>
                <TableCell className="h-13 min-w-21 p-2">
                  <GameStatusBadge status={game.status as GameStatus} />
                </TableCell>
                <TableCell className="h-13 min-w-21 p-2 text-right">
                  {new Date(game.date)
                    .toLocaleDateString("ko-KR", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })
                    .replace(/\. /g, ".")
                    .replace(/\.$/, "")}
                </TableCell>
                <TableCell className="h-13 p-2 text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="게임 삭제"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGame(game);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* 완료된 게임 (최근 7일) */}
      {completedGames && completedGames.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-4 text-muted-foreground">
            완료된 게임 (최근 7일)
          </h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>지점</TableHead>
                <TableHead>반</TableHead>
                <TableHead></TableHead>
                <TableHead className="text-right">완료 일자</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {completedGames.map((game, index) => (
                <TableRow
                  key={game._id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleGameClick(game)}
                >
                  <TableCell className="h-13 min-w-21 p-2 text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell className="h-13 min-w-21 p-2">
                    {game.office.name}
                  </TableCell>
                  <TableCell className="h-13 min-w-21 p-2">
                    {game.group.name}
                  </TableCell>
                  <TableCell className="h-13 min-w-21 p-2">
                    <GameStatusBadge status={game.status as GameStatus} />
                  </TableCell>
                  <TableCell className="h-13 min-w-21 p-2 text-right">
                    {new Date(game.date)
                      .toLocaleDateString("ko-KR", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                      .replace(/\. /g, ".")
                      .replace(/\.$/, "")}
                  </TableCell>
                  <TableCell className="h-13 p-2 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="게임 삭제"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGame(game);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* 게임 생성 다이얼로그 */}
      <CreateGameDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        groups={groups ?? []}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
        loadDate={loadDate}
        onLoadDateChange={setLoadDate}
        onConfirm={handleCreateGame}
        hasTodayGame={hasTodayGame}
        isLoading={createGame.isPending}
      />
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/competition/")({
  component: CompetitionPage,
});
